"use client";

import { type HTMLAttributes, forwardRef } from "react";
import { VStack } from "./layout/stack";
import { cn } from "./lib/utils";
import { Label } from "./typography";

export function Wrapper({
	children,
}: {
	children: React.ReactNode;
}): React.ReactElement {
	return (
		<VStack className="w-full gap-xs rounded-input bg-input-background px-lg py-md text-input-text">
			{children}
		</VStack>
	);
}

export function InputError({
	value,
	className,
}: {
	value: string | string[] | undefined;
	className?: string;
}): React.ReactElement | null {
	if (!value) {
		return null;
	}

	return (
		<span className={cn("w-full px-lg py-sm text-md text-red-500", className)}>
			{value}
		</span>
	);
}

export function InputLabel({
	text,
	required,
}: {
	text: string;
	required?: boolean;
}): React.ReactElement {
	return (
		<Label className="text-text-label">
			{text} {required ? <span className="text-text-signal">*</span> : null}
		</Label>
	);
}

export interface InputProps {
	name?: string;
	defaultValue?: string;
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	value?: string;
	required?: boolean;
	type?: HTMLInputElement["type"];
	readOnly?: boolean;
}

export const Input = forwardRef<
	HTMLInputElement,
	InputProps & HTMLAttributes<HTMLInputElement>
>(
	(
		{
			name,
			label,
			type = "text",
			required,
			placeholder,
			onChange,
			onBlur,
			readOnly,
			className,
			...props
		},
		ref,
	): React.ReactElement => {
		return (
			<Wrapper>
				{label ? <InputLabel required={required} text={label} /> : null}
				<input
					aria-label={name}
					className={cn(
						"text-md text-input-text",
						"placeholder:text-input-text-placeholder",
						"bg-transparent",
						"outline-none",
						className,
					)}
					name={name}
					onBlur={onBlur}
					onChange={onChange}
					placeholder={placeholder}
					readOnly={readOnly}
					ref={ref}
					required={required}
					type={type}
					{...props}
				/>
			</Wrapper>
		);
	},
);

Input.displayName = "Input";
